import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginPage from "./LoginPage";
import DashboardLayout from "./DashboardLayout";
import AdminModule from "./AdminModule";
import HomeModule from "./HomeModule";
import ClientsModule from "./ClientsModule";
import BudgetsModule from "./BudgetsModule";
import ServiceOrdersModule from "./ServiceOrdersModule";
import FinancialModule from "./FinancialModule";
import LeadsModule from "./LeadsModule";
import WebsiteModule from "./WebsiteModule";
// Portfolio + Pricing are rendered inside WebsiteModule via its tabs;
// legacy `pricing`/`portfolio` activeTab IDs redirect to WebsiteModule with the matching initialTab.
import WebmailModule from "./WebmailModule";
import ExpenseTrackerModule from "./ExpenseTrackerModule";
import SalesPipelineModule from "./SalesPipelineModule";
import WorkforceModule from "./WorkforceModule";
import TeamsModule from "./TeamsModule";
import { Toaster } from "sonner";
import usePwaManifest from "../hooks/usePwaManifest";
import useStandaloneLinks from "../hooks/useStandaloneLinks";
import ErrorBoundary from "./ErrorBoundary";

const MODULE_MAP = {
  clients: "clients",
  budgets: "budgets",
  "service-orders": "service_orders",
  financial: "financial",
  leads: "leads",
  website: "portfolio",
  "expense-tracker": "expense_tracker",
  "sales-pipeline": "sales_pipeline",
};

function DashboardContent() {
  const { user, loading, hasPermission, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(null);
  const [invoicePrefill, setInvoicePrefill] = useState(null);
  const [viewInvoiceId, setViewInvoiceId] = useState(null);
  const [webmailPrefill, setWebmailPrefill] = useState(null);
  // Deep-link from Financial → Service Orders edit screen.
  // `referrer` lets us return the user to the tab they came from when they
  // save/cancel — no context lost.
  const [soDeepLinkId, setSoDeepLinkId] = useState(null);
  const [soReferrer, setSoReferrer] = useState(null);

  // Home shortcuts can pass intents (open a sub-tab, prefill a form).
  // Consumed by the target module via the corresponding prop.
  const [serviceOrdersIntent, setServiceOrdersIntent] = useState(null); // { openNew?: bool, openBacklog?: bool }
  const [financialInitialFolder, setFinancialInitialFolder] = useState(null);

  // Set initial tab based on permissions
  useEffect(() => {
    if (!user) return;
    // Everyone (admin + non-admin) lands on HOME first.
    setActiveTab("home");
  }, [user, isAdmin, hasPermission]);

  // Generic navigation helper used by HomeModule cards.
  const goTo = (tabId, intent = {}) => {
    if (tabId === "service-orders" && (intent.openNew || intent.openBacklog || intent.prefillClientId)) {
      setServiceOrdersIntent(intent);
    }
    if (tabId === "financial" && intent.folder) setFinancialInitialFolder(intent.folder);
    setActiveTab(tabId);
  };

  // Cross-module shortcut: Client Details → New Service Order pre-filled.
  const createTicketForClient = (clientId) => {
    if (!clientId) return;
    goTo("service-orders", { openNew: true, prefillClientId: clientId });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  if (!activeTab) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }

  const canEdit = (mod) => hasPermission(mod, "edit");

  const requestInvoiceFromSO = (prefill) => {
    setInvoicePrefill(prefill);
    setViewInvoiceId(null);
    setActiveTab("financial");
  };
  const viewLinkedInvoice = (invoiceId) => {
    setViewInvoiceId(invoiceId);
    setInvoicePrefill(null);
    setActiveTab("financial");
  };
  const composeEmail = (prefill) => {
    setWebmailPrefill(prefill || { to: "", subject: "", body: "" });
    setActiveTab("webmail");
  };

  const openServiceOrderFromFinancial = (orderId) => {
    if (!orderId) return;
    setSoReferrer(activeTab);
    setSoDeepLinkId(orderId);
    setActiveTab("service-orders");
  };
  const handleSoDeepLinkBack = () => {
    const tab = soReferrer || "financial";
    setSoDeepLinkId(null);
    setSoReferrer(null);
    setActiveTab(tab);
  };

  const renderModule = () => {
    switch (activeTab) {
      case "home": return <HomeModule onNavigate={goTo} />;
      case "admin": return isAdmin ? <AdminModule /> : null;
      case "clients": return (
        <ClientsModule
          readOnly={!canEdit("clients")}
          onComposeEmail={composeEmail}
          onCreateTicket={canEdit("service_orders") ? createTicketForClient : null}
        />
      );
      case "budgets": return <BudgetsModule readOnly={!canEdit("budgets")} onComposeEmail={composeEmail} />;
      case "service-orders": return (
        <ServiceOrdersModule
          readOnly={!canEdit("service_orders")}
          onRequestInvoice={requestInvoiceFromSO}
          onViewInvoice={viewLinkedInvoice}
          deepLinkOrderId={soDeepLinkId}
          onDeepLinkConsumed={() => setSoDeepLinkId(null)}
          onBackToReferrer={soReferrer ? handleSoDeepLinkBack : null}
          referrerLabel={soReferrer === "financial" ? "Future Revenue" : null}
          openNewOnMount={serviceOrdersIntent?.openNew || false}
          openBacklogOnMount={serviceOrdersIntent?.openBacklog || false}
          prefillClientId={serviceOrdersIntent?.prefillClientId || null}
          onOpenNewConsumed={() => setServiceOrdersIntent(null)}
        />
      );
      case "financial": return (
        <FinancialModule
          readOnly={!canEdit("financial")}
          prefillInvoice={invoicePrefill}
          viewInvoiceId={viewInvoiceId}
          onPrefillConsumed={() => { setInvoicePrefill(null); setViewInvoiceId(null); }}
          onOpenBudgets={() => setActiveTab("budgets")}
          onOpenServiceOrder={canEdit("service_orders") || hasPermission("service_orders", "view") ? openServiceOrderFromFinancial : null}
          initialFolder={financialInitialFolder}
          onInitialFolderConsumed={() => setFinancialInitialFolder(null)}
        />
      );
      case "leads": return <LeadsModule readOnly={!canEdit("leads")} />;
      case "website": return <WebsiteModule canEdit={canEdit} />;
      // Backwards-compatibility: old direct routes redirect to the unified Website module
      case "pricing": return <WebsiteModule canEdit={canEdit} initialTab="pricing" />;
      case "portfolio": return <WebsiteModule canEdit={canEdit} initialTab="portfolio" />;
      case "webmail": return <WebmailModule prefillCompose={webmailPrefill} onPrefillConsumed={() => setWebmailPrefill(null)} />;
      case "expense-tracker": return <ExpenseTrackerModule readOnly={!canEdit("expense_tracker")} />;
      case "sales-pipeline": return <SalesPipelineModule readOnly={!canEdit("sales_pipeline")} />;
      case "workforce": return isAdmin ? <WorkforceModule /> : null;
      case "teams": return isAdmin ? <TeamsModule /> : null;
      default: return null;
    }
  };

  return (
    <DashboardLayout activeTab={activeTab} onTabChange={setActiveTab}>
      <ErrorBoundary
        scope={`the "${activeTab}" module`}
        resetKey={activeTab}
        onHome={() => setActiveTab("home")}
      >
        {renderModule()}
      </ErrorBoundary>
    </DashboardLayout>
  );
}

const TOAST_OPTIONS = { style: { background: "#fff", border: "1px solid #e5e5e5" } };

export default function AdminApp() {
  usePwaManifest({ manifest: "/api/pwa/manifest/admin.json", title: "GOW Admin" });
  useStandaloneLinks();
  return (
    <AuthProvider>
      <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
      <ErrorBoundary scope="the admin dashboard">
        <DashboardContent />
      </ErrorBoundary>
    </AuthProvider>
  );
}
